<template>
  <div class="chart-container text-center">
    <!-- 1. Success State: Show the Bar Chart -->
    <Bar v-if="usageLoaded" :data="chartData" :options="chartOptions" />

    <!-- 2. Loading State: Show the Spinner -->
    <div v-else-if="loading" class="spinner-border m-5" role="status" style="width: 10rem; height: 10rem;">
      <span class="visually-hidden">Loading Usage...</span>
    </div>

    <!-- 3. Error State: Show a message and a retry option -->
    <div v-else-if="hasError" class="m-5 text-danger">
      <p>Failed to load Data Usage</p>
      <button class="btn btn-sm btn-outline-secondary" @click="loadUsage">
        Retry
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  Colors,
  Title
} from 'chart.js';
import { Bar } from 'vue-chartjs';
import { useAuthStore } from '@/stores/authStore';
import { apiFetch } from '@/utils/api';
import { ApiEndpoint } from '../utils/config';

ChartJS.register(BarElement, Tooltip, Legend, Colors, Title, CategoryScale, LinearScale);

const props = defineProps({
  title: {
    type: String,
    required: false,
    default: 'Top Users'
  },
});

const auth = useAuthStore();

// Reactive State
const loading = ref(false);
const usageLoaded = ref(false);
const hasError = ref(false);

const chartData = reactive({
  labels: [],
  datasets: []
});

const chartOptions = reactive({
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      title: { display: true, text: 'KB' },
    },
    x: {
      title: { display: true, text: 'IMSI (abbr.)' },
      ticks: { maxRotation: 90, minRotation: 90 }
    },
  },
  plugins: {
    title: {
      display: !!props.title,
      text: props.title,
    },
    legend: { position: 'bottom' }
  },
});

const loadUsage = async () => {
  // Initialize state
  loading.value = true;
  usageLoaded.value = false;
  hasError.value = false;

  console.info(`Loading Messages for usage...`);

  const backdate = new Date();
  backdate.setDate(backdate.getDate() - 30);
  const backdateStr = backdate.toISOString().slice(0, 10) + 'T00:00:00Z';

  const queryParams = [`from=${backdateStr}`];
  const query = queryParams.length > 0 ? ('?' + queryParams.join('&')) : '';

  try {
    const { items: usage, ...meta } = await apiFetch(`${ApiEndpoint.MESSAGES}${query}`);
    if (!Array.isArray(usage)) throw new Error(`Invalid usage data (not an array)`);

    // Process data
    const usagePerSim = {};
    usage.forEach(message => {
      const shortImsi = message.imsi.slice(-4);
      if (!(shortImsi in usagePerSim)) usagePerSim[shortImsi] = 0;
      if (message.size) usagePerSim[shortImsi] += message.size / 1000;
    });

    const sortedEntries = Object.entries(usagePerSim)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    chartData.labels = sortedEntries.map(entry => entry[0]);
    chartData.datasets = [{
      label: `since ${backdateStr.replace('T', ' ').replace('Z', ' UTC')}`,
      data: sortedEntries.map(entry => entry[1]),
      backgroundColor: 'rgba(54, 162, 235, 0.5)',
    }];

    usageLoaded.value = true;
  } catch (err) {
    console.error("Failed to load Data Usage:", err);
    hasError.value = true;
  } finally {
    // This always runs, ensuring the spinner stops even on error
    loading.value = false;
  }
};

onMounted(() => {
  loadUsage();
});
</script>

<style scoped>
.chart-container {
  height: 300px;
}
</style>
